import { useMemo } from 'react';
import KpiRow from './echoes/KpiRow';
import EmotionalResonanceChart from './echoes/EmotionalResonanceChart';
import RecentBreakthroughs from './echoes/RecentBreakthroughs';
import { resolveScore, hasLabel } from '../utils/moodScale';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

// A note is MEASURABLE only when both sides carry a real affect label — one of
// the 23 scored emotions, or a legacy 5-bucket mood. A missing label and
// 'not_sure' are both non-answers and are filtered out here, before any math.
//
// Until 2026-09-23 they were not: resolveScore collapsed a missing label to
// neutral (6), so an untagged note drew a point halfway up the chart and pulled
// the Shift KPI toward zero. That was harmless while the picker was mandatory
// and every echo had both sides. The merged capture makes labelling optional,
// so it stops being harmless — hence the filter and the stated denominator.
export const isMeasurable = (e) =>
  hasLabel(e?.emotion_before, e?.mood_before) &&
  hasLabel(e?.emotion_after, e?.mood_after);

// KPI aggregate, over measurable notes only:
//   • Pre  = mean of resolveScore(emotion_before, mood_before) across the 30-day window.
//   • Post = mean of resolveScore(emotion_after, mood_after) across the 30-day window.
//   • Shift = mean of per-echo (postScore − preScore).
//
// resolveScore reads the 23-code emotion first and falls back to the legacy
// 5-bucket mood, matching EmotionalResonanceChart and RecentBreakthroughs — so
// the KPI numbers and the chart directly above them describe the same data.
//
// Fixed 2026-08-10: this read moodToScore(mood_before) alone. Mobile captures
// send emotion_* without mood_*, so every mobile Echo collapsed to the
// 'neutral' fallback (6) and its real movement vanished — an overwhelmed→glad
// Echo (1.5→8.5) was recorded as a shift of zero. On real data the headline
// Shift KPI understated by ~4x while the chart beside it read correctly.
// Superseded 2026-09-23: genuinely absent affect no longer collapses to neutral
// either. It is excluded, and the copy says so. "Missing data should not read as
// worst" was right; the mistake was reading it as "average" instead of as
// "nothing to report".
function computeAggregates(echoes) {
  if (!echoes || echoes.length === 0) {
    return { avgPre: NaN, avgPost: NaN, avgShift: NaN, count: 0 };
  }
  let preSum = 0;
  let postSum = 0;
  let shiftSum = 0;
  let n = 0;
  for (const e of echoes) {
    const pre = resolveScore(e.emotion_before, e.mood_before);
    const post = resolveScore(e.emotion_after, e.mood_after);
    // Belt and braces: callers pass measurable rows, but a null here would
    // otherwise turn every aggregate into NaN silently.
    if (pre === null || post === null) continue;
    preSum += pre;
    postSum += post;
    shiftSum += post - pre;
    n += 1;
  }
  if (n === 0) {
    return { avgPre: NaN, avgPost: NaN, avgShift: NaN, count: 0 };
  }
  return {
    avgPre: preSum / n,
    avgPost: postSum / n,
    avgShift: shiftSum / n,
    count: n,
  };
}

// The list from VoiceTab may include soft-deleted or pending rows. For the
// dashboard we want anything with a valid created_at inside the 30-day window.
// Labelling is checked later, by isMeasurable — this step is about time only.
function windowEchoes(echoes, now = Date.now()) {
  if (!echoes || echoes.length === 0) return [];
  const cutoff = now - THIRTY_DAYS_MS;
  return echoes.filter((e) => {
    const ts = new Date(e.created_at).getTime();
    return Number.isFinite(ts) && ts >= cutoff;
  });
}

// Local calendar day key — YYYY-MM-DD in the user's timezone. entry_date is
// preferred when the server already denormalized it; otherwise derive from
// created_at. Using local time keeps "today" aligned with what the user sees
// on their EchoCard timestamps.
function dayKey(echo) {
  if (echo.entry_date) return echo.entry_date;
  const ts = new Date(echo.created_at);
  if (Number.isNaN(ts.getTime())) return '';
  const y = ts.getFullYear();
  const m = String(ts.getMonth() + 1).padStart(2, '0');
  const d = String(ts.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Collapse multiple echoes recorded on the same calendar day down to the
// single latest one. Ordering rule: max(created_at) wins. Applied globally so
// KPI, chart, and breakthroughs stay consistent — otherwise a user with three
// echoes on Monday would see three chart points but one Monday breakthrough.
function latestPerDay(echoes) {
  if (!echoes || echoes.length === 0) return [];
  const byDay = new Map();
  for (const e of echoes) {
    const key = dayKey(e);
    if (!key) continue;
    const ts = new Date(e.created_at).getTime();
    if (!Number.isFinite(ts)) continue;
    const existing = byDay.get(key);
    if (!existing || ts > existing._ts) {
      byDay.set(key, { ...e, _ts: ts });
    }
  }
  // Strip the private _ts and return in chronological order.
  return Array.from(byDay.values())
    .sort((a, b) => a._ts - b._ts)
    .map(({ _ts, ...rest }) => rest);
}

export default function EchoesDashboard({ echoes, onViewTimeline, showIntro = true }) {
  const windowed = useMemo(() => windowEchoes(echoes), [echoes]);
  const daily = useMemo(() => latestPerDay(windowed), [windowed]);
  // Two populations, deliberately: `daily` is every day with a note (the
  // denominator the user is told about), `measurable` is the subset that can
  // honestly be plotted.
  const measurable = useMemo(() => daily.filter(isMeasurable), [daily]);
  const { avgPre, avgPost, avgShift, count } = useMemo(
    () => computeAggregates(measurable),
    [measurable]
  );

  if (count === 0) return null;

  return (
    <section
      data-testid="echoes-dashboard"
      aria-label="Echoes impact dashboard"
      className="space-y-4 sm:space-y-6 mb-6 sm:mb-8"
    >
      {showIntro && (
      <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed px-1">
        {count === daily.length
          ? 'How speaking shifts you, across your last 30 days.'
          : `How speaking shifts you — from the ${count} of ${daily.length} notes you labelled before and after.`}
      </p>
      )}
      <KpiRow avgPre={avgPre} avgPost={avgPost} avgShift={avgShift} />
      <EmotionalResonanceChart echoes={measurable} />
      <RecentBreakthroughs echoes={measurable} onViewTimeline={onViewTimeline} />
    </section>
  );
}

export { computeAggregates, windowEchoes, latestPerDay };
